import React, { useState, useEffect, useCallback } from 'react';
import { Plus, CheckCircle2, Circle, Trash2, Loader2 } from 'lucide-react';
import api from '../../utils/api';
import ExportExcelButton from '../ExportExcelButton';
import { getLocalDateInputValue, formatRecordDate } from '../../utils/dateUtils';
import {
    PAYMENT_TYPES, PAYMENT_TYPE_LABELS, CURRENCIES, formatAmount, isNegativePaymentType,
} from '../../utils/personnelLabels';
import toast from 'react-hot-toast';

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 6 }, (_, i) => currentYear - i);

const PaymentsTab = ({ personnel }) => {
    const personnelId = personnel.id;
    const [payments, setPayments] = useState([]);
    const [totals, setTotals] = useState([]);
    const [year, setYear] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [form, setForm] = useState({
        paymentDate: getLocalDateInputValue(),
        amount: personnel.weeklyWage ? String(personnel.weeklyWage) : '',
        currency: personnel.wageCurrency || 'TRY',
        type: 'haftalik',
        isPaid: false,
        note: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const fetchPayments = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await api.get(`/personnel/${personnelId}/payments`, { params: { year } });
            setPayments(res.data.data || []);
            setTotals(res.data.totals || []);
        } catch (error) {
            toast.error('Ödemeler yüklenemedi.');
        } finally {
            setIsLoading(false);
        }
    }, [personnelId, year]);

    useEffect(() => { fetchPayments(); }, [fetchPayments]);

    const handleAdd = async (e) => {
        e.preventDefault();
        if (!form.amount || parseFloat(form.amount) < 0) {
            toast.error('Geçerli bir tutar girin.');
            return;
        }
        const duplicate = payments.some((payment) => payment.paymentDate === form.paymentDate
            && payment.type === form.type
            && payment.currency === form.currency
            && Number(payment.amount) === Number(form.amount));
        if (duplicate && !window.confirm('Aynı tarih, tür ve tutarda bir ödeme zaten var. Yine de eklensin mi?')) return;
        setIsSubmitting(true);
        try {
            await api.post(`/personnel/${personnelId}/payments`, {
                ...form,
                amount: parseFloat(form.amount),
                isPaid: form.isPaid ? 1 : 0,
            });
            toast.success('Ödeme eklendi.');
            setForm({
                paymentDate: getLocalDateInputValue(),
                amount: personnel.weeklyWage ? String(personnel.weeklyWage) : '',
                currency: personnel.wageCurrency || form.currency,
                type: 'haftalik',
                isPaid: false,
                note: '',
            });
            fetchPayments();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Ödeme eklenemedi.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleTogglePaid = async (payment) => {
        try {
            await api.patch(`/personnel/payments/${payment.id}/toggle-paid`);
            setPayments((prev) => prev.map((p) => p.id === payment.id ? { ...p, isPaid: p.isPaid ? 0 : 1 } : p));
            fetchPayments();
        } catch (error) {
            toast.error('Durum güncellenemedi.');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Bu ödeme kaydını silmek istediğinize emin misiniz?')) return;
        try {
            await api.delete(`/personnel/payments/${id}`);
            toast.success('Ödeme silindi.');
            fetchPayments();
        } catch (error) {
            toast.error('Silinemedi.');
        }
    };

    // Toplamlari para birimi bazinda odenen/bekleyen olarak grupla
    const totalsByCurrency = {};
    totals.forEach((t) => {
        if (!totalsByCurrency[t.currency]) totalsByCurrency[t.currency] = { paid: 0, pending: 0 };
        if (t.isPaid) totalsByCurrency[t.currency].paid = t.total || 0;
        else totalsByCurrency[t.currency].pending = t.total || 0;
    });

    const excelMapper = (row) => ({
        'Tarih': formatRecordDate(row.paymentDate),
        'Tür': PAYMENT_TYPE_LABELS[row.type] || row.type,
        'Tutar': isNegativePaymentType(row.type) ? -row.amount : row.amount,
        'Para Birimi': row.currency,
        'Durum': row.isPaid ? 'Ödendi' : 'Bekliyor',
        'Not': row.note || '-',
    });

    const inputClass = 'px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg focus:outline-none focus:border-primary-500 text-white text-sm';

    return (
        <div className="space-y-5">
            {/* Toplamlar */}
            <div className="flex flex-wrap gap-3">
                {Object.keys(totalsByCurrency).length === 0 ? (
                    <div className="text-sm text-dark-muted">Henüz ödeme kaydı yok.</div>
                ) : (
                    Object.entries(totalsByCurrency).map(([cur, v]) => (
                        <div key={cur} className="glass border border-dark-border rounded-xl px-4 py-3 min-w-[160px]">
                            <div className="text-xs text-dark-muted uppercase tracking-wider mb-1">{cur}</div>
                            <div className="text-sm text-green-400 font-semibold">Ödenen: {formatAmount(v.paid)}</div>
                            <div className="text-sm text-orange-400 font-semibold">Bekleyen: {formatAmount(v.pending)}</div>
                        </div>
                    ))
                )}
            </div>

            {/* Ekleme formu */}
            <form onSubmit={handleAdd} className="glass border border-dark-border rounded-xl p-4 grid grid-cols-2 md:grid-cols-6 gap-3 items-end">
                <div className="flex flex-col gap-1">
                    <label className="text-xs text-dark-muted">Tarih</label>
                    <input type="date" value={form.paymentDate} onChange={(e) => setForm({ ...form, paymentDate: e.target.value })} className={inputClass} />
                </div>
                <div className="flex flex-col gap-1">
                    <label className="text-xs text-dark-muted">Tutar</label>
                    <input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className={inputClass} placeholder="0.00" />
                </div>
                <div className="flex flex-col gap-1">
                    <label className="text-xs text-dark-muted">Para Birimi</label>
                    <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className={inputClass}>
                        {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                </div>
                <div className="flex flex-col gap-1">
                    <label className="text-xs text-dark-muted">Tür</label>
                    <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={inputClass}>
                        {PAYMENT_TYPES.map((t) => <option key={t} value={t}>{PAYMENT_TYPE_LABELS[t]}</option>)}
                    </select>
                </div>
                <div className="flex flex-col gap-1 col-span-2 md:col-span-1">
                    <label className="text-xs text-dark-muted">Not</label>
                    <input type="text" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className={inputClass} placeholder="Opsiyonel" />
                </div>
                <div className="flex items-center gap-3 col-span-2 md:col-span-1">
                    <label className="flex items-center gap-1.5 text-xs text-dark-muted cursor-pointer">
                        <input type="checkbox" checked={form.isPaid} onChange={(e) => setForm({ ...form, isPaid: e.target.checked })} />
                        Ödendi
                    </label>
                    <button type="submit" disabled={isSubmitting} className="flex items-center gap-1 bg-primary-600 hover:bg-primary-500 text-white px-3 py-2 rounded-lg text-sm font-semibold disabled:opacity-50">
                        <Plus size={16} /> Ekle
                    </button>
                </div>
            </form>

            {/* Filtre + Excel */}
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <select value={year} onChange={(e) => setYear(e.target.value)} className={inputClass}>
                    <option value="">Tüm Yıllar</option>
                    {YEAR_OPTIONS.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
                <ExportExcelButton data={payments} mapper={excelMapper} sheetName="Odemeler" filename="Personel_Odemeler.xlsx" label="EXCEL" />
            </div>

            {/* Liste */}
            <div className="glass border border-dark-border rounded-xl overflow-hidden">
                <div className="mobile-table-hint lg:hidden">Tüm ödeme ayrıntıları için tabloyu yatay kaydırın →</div>
                <div className="mobile-table-scroll overflow-x-auto">
                    <table className="mobile-complete-table w-full text-left text-sm whitespace-nowrap">
                        <thead className="bg-dark-card text-dark-muted border-b border-dark-border uppercase tracking-wider text-[11px]">
                            <tr>
                                <th className="px-4 py-3 font-semibold">Tarih</th>
                                <th className="px-4 py-3 font-semibold">Tür</th>
                                <th className="px-4 py-3 font-semibold">Tutar</th>
                                <th className="px-4 py-3 font-semibold text-center">Durum</th>
                                <th className="px-4 py-3 font-semibold">Not</th>
                                <th className="px-4 py-3 font-semibold text-right">İşlem</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-dark-border">
                            {isLoading ? (
                                <tr><td colSpan="6" className="px-4 py-8 text-center text-dark-muted"><Loader2 className="animate-spin inline" size={20} /></td></tr>
                            ) : payments.length === 0 ? (
                                <tr><td colSpan="6" className="px-4 py-8 text-center text-dark-muted">Ödeme kaydı bulunamadı.</td></tr>
                            ) : (
                                payments.map((p) => (
                                    <tr key={p.id} className="hover:bg-dark-hover transition-colors">
                                        <td className="px-4 py-3 text-dark-muted">{formatRecordDate(p.paymentDate)}</td>
                                        <td className="px-4 py-3">{PAYMENT_TYPE_LABELS[p.type] || p.type}</td>
                                        <td className={`px-4 py-3 font-semibold ${isNegativePaymentType(p.type) ? 'text-red-400' : 'text-dark-text'}`}>
                                            {isNegativePaymentType(p.type) ? '−' : ''}{formatAmount(p.amount)} <span className="text-xs text-dark-muted">{p.currency}</span>
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            <button onClick={() => handleTogglePaid(p)} className={p.isPaid ? 'text-primary-500' : 'text-dark-muted hover:text-dark-text'} title={p.isPaid ? 'Ödendi' : 'Bekliyor'}>
                                                {p.isPaid ? <CheckCircle2 size={22} /> : <Circle size={22} />}
                                            </button>
                                        </td>
                                        <td className="px-4 py-3 text-dark-muted max-w-[160px] truncate" title={p.note}>{p.note || '-'}</td>
                                        <td className="px-4 py-3 text-right">
                                            <button onClick={() => handleDelete(p.id)} className="p-1.5 text-dark-muted hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors">
                                                <Trash2 size={16} />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default PaymentsTab;
